import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { TemplateClassNames } from '../../../types/template-layouts';
import { EIcon } from '../icon/icon';
import './image-preview.scss';

export interface ImagePreviewOption {
	imageUrl: string;
}
interface ImagePreviewProps {
	options: ImagePreviewOption[];
	arialLabel?: string;
	template?: TemplateClassNames;
}
const PAGE_SIZE = 3;
export const ImagePreview = ({ options, arialLabel, template }: ImagePreviewProps): JSX.Element => {
	const [index, setIndex] = useState<number>(0);

	const visibleImages = useMemo(() => {
		const start = Math.floor(index / PAGE_SIZE) * PAGE_SIZE;
		return options.slice(start, start + PAGE_SIZE);
	}, [index, options]);

	const selectedOption = useMemo(() => {
		return options[index];
	}, [options, index]);

	return (
		<div
			className={classNames('image-preview', template)}
			aria-roledescription="carousel"
			aria-label={arialLabel}
		>
			{selectedOption && (
				<div className="image-preview-main-pic">
					<img src={selectedOption.imageUrl} alt="" width="164" height="164" aria-hidden="true" />
				</div>
			)}

			<div className="image-preview-carousel">
				{index !== 0 && (
					<button
						className="image-carousel-control left"
						onClick={() => setIndex(Math.max(index - 1, 0))}
						aria-label="Previous image"
						aria-controls="image-preview-grid"
					>
						<EIcon name="keyboard-arrow-left" size="giant" />
					</button>
				)}
				{index + 1 < options.length && (
					<button
						className="image-carousel-control right"
						onClick={() => setIndex(Math.min(index + 1, options.length - 1))}
						aria-label="Next image"
						aria-controls="image-preview-grid"
					>
						<EIcon name="keyboard-arrow-right" size="giant" />
					</button>
				)}
				<ul className="image-preview-grid" id="image-preview-grid" aria-live="polite">
					{visibleImages.map((option, i) => (
						<ImageOption
							key={option.imageUrl}
							option={option}
							isSelected={option === selectedOption}
							ariaLabel={`${i + 1} of ${options.length}`}
							onClick={(option) => setIndex(options.indexOf(option))}
						/>
					))}
				</ul>
			</div>
		</div>
	);
};

interface ImageOptionProps {
	option: ImagePreviewOption;
	isSelected: boolean;
	ariaLabel?: string;
	onClick: (option: ImagePreviewOption) => void;
}
const ImageOption = ({
	option,
	onClick,
	isSelected,
	ariaLabel,
}: ImageOptionProps): JSX.Element => {
	return (
		<li
			className={classNames({
				'selected': isSelected,
			})}
			role="group"
			aria-roledescription="slide"
			aria-label={ariaLabel}
			onClick={() => onClick(option)}
		>
			<img src={option.imageUrl} alt="" width="50" height="53" aria-hidden="true" />
		</li>
	);
};
